import confetti from "canvas-confetti";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Accordion, Alert, Button, Container } from "react-bootstrap";
import {
	DndContext,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	SortableContext,
	arrayMove,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import SortableExerciseItem from "./SortableExerciseItem";
import { isToday } from "date-fns";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "../api/api";
import authService from "../services/authService";
import {
	WALKTHROUGH_PREVIEW_VIEWED_EVENT,
	WALKTHROUGH_REVIEW_CUE_EVENT,
} from "../utils/walkthroughEvents";
import ClientProgramHeader from "./ClientProgramHeader";
import SuccessPopup from "./SuccessPopup";
import TherapistProgramHeader from "./TherapistProgramHeader";
import "../css/ProgramDetails.css";

const CELEBRATION_SOUND_STORAGE_KEY =
	"plasticise_celebration_sound_enabled";

const successSound = new Audio("/success.mp3");
successSound.volume = 0.033;

const ProgramDetails = () => {
	const videoRef = useRef(null);
	const [program, setProgram] = useState(null);
	const [loading, setLoading] = useState(true);
	const [exercises, setExercises] = useState([]);
	const [, setSavingOrder] = useState(false);
	const [bulkCompleting, setBulkCompleting] = useState(false);
	const [error, setError] = useState(null);
	const { programId } = useParams();
	const isTherapist = authService.isTherapist();
	const navigate = useNavigate();
	const [completedExercises, setCompletedExercises] = useState(new Set());
	const [historyExercises, setHistoryExercises] = useState(new Set());
	const [feedbackRefreshKey, setFeedbackRefreshKey] = useState(0);
	const location = useLocation();
	const initialPreview = isTherapist && location.state?.previewClient === true;
	const [activeKey, setActiveKey] = useState(initialPreview ? "0" : null);
	const [searchParams] = useSearchParams();
	const [selectedDate, setSelectedDate] = useState(
		searchParams.get("date") || new Date().toISOString().split("T")[0],
	);
	const [showSuccessPopup, setShowSuccessPopup] = useState(false);
	const [successPopupMode, setSuccessPopupMode] = useState("exercise");
	const [isClientPreview, setIsClientPreview] = useState(initialPreview);
	const previewEndRef = useRef(null);
	const [celebrationSoundEnabled, setCelebrationSoundEnabled] = useState(() => {
		const storedPreference = localStorage.getItem(CELEBRATION_SOUND_STORAGE_KEY);

		if (storedPreference === null) {
			return true;
		}

		return storedPreference === "true";
	});

	const refreshFeedbackState = useCallback(async () => {
		const completedResponse = await api.get(
			`/feedback/completed/?program=${programId}&date=${selectedDate}`
		);

		setCompletedExercises(new Set(completedResponse.data.exercise_ids || []));
		setHistoryExercises(new Set(completedResponse.data.history_exercise_ids || []));
		setFeedbackRefreshKey((current) => current + 1);
	}, [programId, selectedDate]);

	useEffect(() => {
		const fetchProgram = async () => {
			try {
				setLoading(true);
				setError(null);

				const selectedDateValue = new Date(`${selectedDate}T12:00:00`);
				const datetime = isToday(selectedDateValue)
					? new Date().toISOString()
					: selectedDateValue.toISOString();

				const [response, completedResponse] = await Promise.all([
					api.get(
						`/programs/${programId}/historical/?datetime=${datetime}`
					),
					api.get(
						`/feedback/completed/?program=${programId}&date=${selectedDate}`
					),
				]);

				setProgram(response.data);

				const orderedExercises = [...(response.data.exercises || [])].sort((a, b) => {
					const indexA = Number.isFinite(a.index) ? a.index : 0;
					const indexB = Number.isFinite(b.index) ? b.index : 0;

					if (indexA !== indexB) return indexA - indexB;
					return a.id - b.id;
				});

				setExercises(orderedExercises);
				setCompletedExercises(
					new Set(completedResponse.data.exercise_ids || [])
				);
				setHistoryExercises(
					new Set(completedResponse.data.history_exercise_ids || [])
				);
				setFeedbackRefreshKey((current) => current + 1);
			} catch (err) {
				console.error("Error fetching program details:", err);
				setError("Failed to load program details");
			} finally {
				setLoading(false);
			}
		};

		fetchProgram();
	}, [programId, selectedDate, refreshFeedbackState]);

	const handleAccordionSelect = (eventKey) => {
		setActiveKey(eventKey === activeKey ? null : eventKey);
	};

	useEffect(() => {
		const handleWalkthroughReviewCue = (event) => {
			if (event.detail?.stepId === "preview") {
				setIsClientPreview(false);
			}
		};

		window.addEventListener(
			WALKTHROUGH_REVIEW_CUE_EVENT,
			handleWalkthroughReviewCue
		);

		return () => {
			window.removeEventListener(
				WALKTHROUGH_REVIEW_CUE_EVENT,
				handleWalkthroughReviewCue
			);
		};
	}, []);

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		})
	);

	const incompleteExercises = exercises.filter(
		(exercise) => !completedExercises.has(exercise.id)
	);

	const allExercisesCompleted =
		exercises.length > 0 && incompleteExercises.length === 0;

	const handleDragEnd = async (event) => {
		const { active, over } = event;

		if (!over || active.id === over.id) return;

		const oldIndex = exercises.findIndex((exercise) => exercise.id === active.id);
		const newIndex = exercises.findIndex((exercise) => exercise.id === over.id);

		if (oldIndex === -1 || newIndex === -1) return;

		const previousExercises = exercises;

		const nextExercises = arrayMove(exercises, oldIndex, newIndex).map(
			(exercise, index) => ({
				...exercise,
				index,
			})
		);

		setExercises(nextExercises);

		try {
			setSavingOrder(true);

			await api.post(`/programs/${programId}/reorder-exercises/`, {
				exercise_ids: nextExercises.map((exercise) => exercise.id),
			});
		} catch (err) {
			console.error("Error saving exercise order:", err);
			setExercises(previousExercises);
			setError("Failed to save activity order");
		} finally {
			setSavingOrder(false);
		}
	};

	const handleExerciseComplete = (exerciseId) => {
		const newCompletedExercises = new Set([...completedExercises, exerciseId]);
		setCompletedExercises(newCompletedExercises);
		setHistoryExercises(new Set([...historyExercises, exerciseId]));

		if (!completedExercises.has(exerciseId)) {
			setActiveKey(null);
			setSuccessPopupMode("exercise");
			setShowSuccessPopup(true);
			setFeedbackRefreshKey((current) => current + 1);
		}
	};

	const handleToggleCelebrationSound = () => {
		setCelebrationSoundEnabled((current) => {
			const nextValue = !current;
			localStorage.setItem(CELEBRATION_SOUND_STORAGE_KEY, String(nextValue));

			if (!nextValue) {
				successSound.pause();
				successSound.currentTime = 0;
			}

			return nextValue;
		});
	};

	const playCompletionCelebration = () => {
		if (celebrationSoundEnabled) {
			successSound.pause();
			successSound.currentTime = 0;
			successSound.volume = 0.033;
			successSound.play().catch((err) => {
				console.warn("Could not play success sound:", err);
			});
		}

		const prefersReducedMotion = window.matchMedia?.(
			"(prefers-reduced-motion: reduce)"
		).matches;

		if (!prefersReducedMotion) {
			confetti({
				particleCount: 70,
				spread: 65,
				origin: { y: 0.65 },
			});
		}
	};

	const handleCompleteAllExercises = async () => {
		if (bulkCompleting || allExercisesCompleted) return;

		try {
			setBulkCompleting(true);
			setError(null);

			const formData = new FormData();
			formData.append("program", programId);
			formData.append("date_completed", selectedDate);
			formData.append("effort_rating", "0");
			formData.append("feeling", "");

			const response = await api.post("/feedback/complete-program/", formData);

			if (response.data.exercise_ids) {
				setCompletedExercises(new Set(response.data.exercise_ids));
				setHistoryExercises(
					new Set([
						...Array.from(historyExercises),
						...response.data.exercise_ids,
					])
				);
			}

			await refreshFeedbackState();
			setActiveKey(null);
			playCompletionCelebration();
			setSuccessPopupMode("program");
			setShowSuccessPopup(true);
		} catch (err) {
			console.error("Error completing all exercises:", err.response?.data || err);
			setError(
				err.response?.data?.detail ||
					err.response?.data?.error ||
					"Failed to complete all activities"
			);
		} finally {
			setBulkCompleting(false);
		}
	};

	const handleSelectedDateChange = (date) => {
		setActiveKey(null);
		setSelectedDate(date);

		const nextSearchParams = new URLSearchParams(window.location.search);
		nextSearchParams.set("date", date);

		window.history.replaceState(
			window.history.state,
			"",
			`${window.location.pathname}?${nextSearchParams.toString()}${window.location.hash}`,
		);
	};

	const handleProgramDelete = () => {
		navigate(`/client/${program.client.user.id}`);
	};

	const handleClientPreview = () => {
		setIsClientPreview(true);
		setActiveKey(exercises.length > 0 ? "0" : null);
	};

	useEffect(() => {
		if (!isClientPreview || program?.is_draft) return undefined;
		let requested = false;
		let cancelled = false;
		let hasScrolled = false;
		const initialScrollY = window.scrollY;

		const checkViewed = () => {
			const end = previewEndRef.current;
			if (!end || requested || cancelled) return;
			const viewportHeight = window.visualViewport?.height || window.innerHeight;
			const rect = end.getBoundingClientRect();
			const previewRect = end.closest('[data-walkthrough-client-preview="true"]')?.getBoundingClientRect();
			// A short program that already fits on screen needs no impossible scroll.
			const previewFits = previewRect?.height > 0 && previewRect.top >= 0 && previewRect.bottom <= viewportHeight;
			if ((!hasScrolled && !previewFits) || rect.top > viewportHeight || rect.bottom < 0) return;
			requested = true;
			api.post("/therapist-walkthrough/", { action: "preview" })
				.then(() => {
					if (!cancelled) window.dispatchEvent(new CustomEvent(WALKTHROUGH_PREVIEW_VIEWED_EVENT, {
						detail: { programId },
					}));
				})
				.catch((previewError) => {
					requested = false;
					console.error("Could not record therapist client preview:", previewError);
				});
		};
		const onScroll = (event) => {
			const preview = previewEndRef.current?.closest('[data-walkthrough-client-preview="true"]');
			if (window.scrollY > initialScrollY || (preview?.contains(event.target) && event.target.scrollTop > 0)) {
				hasScrolled = true;
			}
			checkViewed();
		};
		window.addEventListener("scroll", onScroll, { capture: true, passive: true });
		window.addEventListener("resize", checkViewed);
		window.visualViewport?.addEventListener("resize", checkViewed);
		const observer = typeof IntersectionObserver === "function" ? new IntersectionObserver(checkViewed) : null;
		if (previewEndRef.current) observer?.observe(previewEndRef.current);
		checkViewed();
		return () => {
			cancelled = true;
			observer?.disconnect();
			window.removeEventListener("scroll", onScroll, true);
			window.removeEventListener("resize", checkViewed);
			window.visualViewport?.removeEventListener("resize", checkViewed);
		};
	}, [isClientPreview, program?.is_draft, programId]);

	if (loading && !program) {
		return (
			<Container className="mt-4">
				<Alert variant="info">Loading program details...</Alert>
			</Container>
		);
	}

	if (error && !program) {
		return (
			<Container className="mt-4">
				<Alert variant="danger">{error}</Alert>
			</Container>
		);
	}

	return (
		<Container className="program-details-page py-3 mb-5"
      data-walkthrough-client-preview={isClientPreview ? "true" : undefined}>
			{(!isTherapist || isClientPreview) && (
				<ClientProgramHeader
					selectedDate={selectedDate}
					programName={program.name}
					onBack={isClientPreview ? () => setIsClientPreview(false) : undefined}
					isPreview={isClientPreview}
				/>
			)}

			{isTherapist && !isClientPreview && (
				<TherapistProgramHeader
					selectedDate={selectedDate}
					program={program}
					onProgramDelete={handleProgramDelete}
					onDateChange={handleSelectedDateChange}
					onPreviewClient={handleClientPreview}
				/>
			)}

			<div className="program-details-exercise-heading">
				<h4 className="mb-0">Activities</h4>
				{!isTherapist && (
					<p className="exercise-helper-text">
						Tap an activity to view it and mark it complete.
					</p>
				)}
				{isClientPreview && (
					<p className="exercise-helper-text">
						This preview is read only. Tap an activity to see its media and instructions.
					</p>
				)}
			</div>

			{isTherapist && !isClientPreview && exercises.length > 0 && (
				<div className="exercise-reorder-hint">
					Drag activities by the handle to reorder them.
				</div>
			)}

			{error && (
				<Alert variant="danger" className="mb-3">
					{error}
				</Alert>
			)}

			{exercises.length > 0 ? (
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragEnd={handleDragEnd}
				>
					<SortableContext
						items={exercises.map((exercise) => exercise.id)}
						strategy={verticalListSortingStrategy}
					>
						<Accordion
							className="program-exercise-list"
							activeKey={activeKey}
							onSelect={handleAccordionSelect}
						>
							{exercises.map((exercise, index) => (
								<SortableExerciseItem
									key={`${exercise.id}-${feedbackRefreshKey}`}
									exercise={exercise}
									index={index}
									activeKey={activeKey}
									completedExercises={completedExercises}
									historyExercises={historyExercises}
									feedbackRefreshKey={feedbackRefreshKey}
									isTherapist={isTherapist && !isClientPreview}
									readOnlyPreview={isClientPreview}
									selectedDate={selectedDate}
									videoRef={videoRef}
									navigate={navigate}
									handleExerciseComplete={handleExerciseComplete}
								/>
							))}
						</Accordion>
					</SortableContext>
				</DndContext>
			) : (
				<Alert variant="info">
					No activities have been added to this program yet.
				</Alert>
			)}

			{!isTherapist && exercises.length > 0 && !allExercisesCompleted && (
				<div className="session-complete-prompt">
					<button
						type="button"
						className="celebration-sound-toggle"
						onClick={handleToggleCelebrationSound}
						aria-pressed={!celebrationSoundEnabled}
						aria-label={
							celebrationSoundEnabled
								? "Disable celebration sound"
								: "Enable celebration sound"
						}
					>
						<span aria-hidden="true">{celebrationSoundEnabled ? "🔊" : "🔇"}</span>
						<span>{celebrationSoundEnabled ? "Sound on" : "Sound off"}</span>
					</button>

					<Button
						variant="primary"
						className="session-complete-button"
						onClick={handleCompleteAllExercises}
						disabled={bulkCompleting}
					>
						<span aria-hidden="true">✓</span>
						<span>{bulkCompleting ? "Marking complete..." : "Mark all complete"}</span>
					</Button>
				</div>
			)}

			{!isTherapist && exercises.length > 0 && allExercisesCompleted && (
				<div className="session-complete-success" role="status">
					<span className="session-complete-success-icon" aria-hidden="true">✓</span>
					<span>All activities completed for today</span>
				</div>
			)}

			{isTherapist && <div ref={previewEndRef} data-walkthrough="finish-onboarding" style={{ minHeight: "1px" }} />}
			<SuccessPopup
				show={showSuccessPopup}
				mode={successPopupMode}
				onHide={() => setShowSuccessPopup(false)}
			/>
		</Container>
	);
};

export default ProgramDetails;
